import { useEffect } from "react";
import * as Sentry from "@sentry/react";

export function useSentryInit(): void {
  useEffect(() => {
    if ((window as any).__sentryInitialized) {
      return;
    }
    (window as any).__sentryInitialized = true;

    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      release: process.env.REACT_APP_REPOSITORY_DATE,
      environment: process.env.NODE_ENV,
      enabled: process.env.NODE_ENV === "production",
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // —— Session buffering & flush timing ——
          // Minimum delay between automatic flushes (ms)
          flushMinDelay: 1_000,
          // Maximum delay allowed before flushing pending data (ms)
          flushMaxDelay: 5_000,

          // —— Session length bounds ——
          // Don’t send sessions shorter than 5 s
          minReplayDuration: 5_000,
          // Cap each session at 5 min
          maxReplayDuration: 300_000,

          // —— Persistence across reloads ——
          stickySession: true, // keep recording even through full-page reloads
          useCompression: true, // compress payloads in a Worker before send

          // —— DOM-mutation limits ——
          // Breadcrumbs recorded from DOM mutations (impact on memory)
          mutationBreadcrumbLimit: 1_000,
          // Absolute cap on mutations per session
          mutationLimit: 5_000,

          // —— Slow-interaction detection ——
          // How long before a click is considered “slow” (ms)
          slowClickTimeout: 7_000,
          // Don’t treat interactions on these selectors as “slow”
          slowClickIgnoreSelectors: [".no-sentry"],

          // —— Network capture ——
          // Only capture request/response bodies for known-safe origins
          networkDetailAllowUrls: [window.location.origin, "https://newapp.haulpay.io"],
          networkCaptureBodies: false, // keeps payload overhead low
          // You can whitelist extra headers if needed:
          // networkRequestHeaders: ["Authorization"],
          // networkResponseHeaders: ["X-My-App-Info"],

          // —— Privacy & PII-handling ——
          maskAllText: true, // turn all text to “*” :contentReference[oaicite:0]{index=0}
          maskAllInputs: true, // mask `<input>` values
          blockAllMedia: true, // don’t embed `<img>`/`<video>` etc. :contentReference[oaicite:1]{index=1}
          // Additional element-level rules:
          mask: [".mask-this"], // mask these selectors in addition to defaults
          unmask: [".unmask-this"], // reveal these despite `maskAllText`
          block: [".block-this"], // hide these selectors in addition to defaults
          unblock: [".unblock-this"], // allow these despite `blockAllMedia`
          ignore: [".no-replay"] // skip events on these inputs
        })
      ],
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0
    });
  }, []);
}
